import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

import { addDays, getTodayAtMidnight } from '@/features/dates/date-helpers';
import { selectProperty } from '@/features/property/redux/selectors';
import { useUser } from '@/features/users/context/userContext';
import type { RecReviewStatus } from '@/graphql/gql-types';
import { useAppSelector } from '@/redux/hooks';
import { generateDatesFromRange } from '../bulk-form-utils';
import { useCreateBulkRecsMutation } from '../gql/_gen_/property-rec-rates.gql';
import { bulkRmRecsSchema, daySelectionPresets } from '../zod/bulk-form-schema';
import { BulkConfirmationDialog } from './bulk-confirmation-dialog';
import { DateRangeFields } from './date-range-fields';
import { DaysOfWeekSelector } from './days-of-week-selector';

type BulkFormValues = z.infer<typeof bulkRmRecsSchema>;

// Status options for the dropdown (matching RecReviewStatus enum)
const STATUS_OPTIONS = [
	{ value: 'R', label: 'Review' },
	{ value: 'P', label: 'Pending' },
];

interface BulkRateFormProps {
	isVisible?: boolean;
}

export function BulkRateForm({ isVisible = true }: BulkRateFormProps) {
	const { user } = useUser();
	const property = useAppSelector(selectProperty);
	const [createBulkRecs] = useCreateBulkRecsMutation({
		refetchQueries: ['GetPropertyRecRates'],
	});

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [pendingData, setPendingData] = useState<BulkFormValues | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [conflicts, setConflicts] = useState<Array<{
		date: string;
		existingRate?: number | undefined;
		message: string;
	}> | null>(null);

	const form = useForm<BulkFormValues>({
		resolver: zodResolver(bulkRmRecsSchema),
		defaultValues: {
			startDate: getTodayAtMidnight(),
			endDate: (() => {
				const result = addDays(1, getTodayAtMidnight());
				return result?.toDate
					? result.toDate()
					: new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
			})(),
			rate: undefined,
			status: 'R', // Default to 'Review'
			notes: '',
			daysOfWeek: daySelectionPresets.all,
		},
	});

	const handleFormSubmit = (data: BulkFormValues) => {
		// Store the data and show confirmation dialog
		setPendingData(data);
		setConflicts(null); // Clear any previous conflicts
		setConfirmDialogOpen(true);
	};

	const handleConfirm = async (allowOverwrite = false) => {
		if (!pendingData) return;

		// Check if we have a valid user
		const userName = user?.login_id;
		if (!userName) {
			toast.error('User not found - cannot submit bulk recommendations');
			setConfirmDialogOpen(false);
			setPendingData(null);
			setConflicts(null);
			return;
		}

		setIsSubmitting(true);
		try {
			// Generate the dates array from the form data
			const dates = generateDatesFromRange(
				pendingData.startDate,
				pendingData.endDate,
				pendingData.daysOfWeek,
			);

			// Call the GraphQL mutation
			const result = await createBulkRecs({
				variables: {
					input: {
						propertyId: property.propertyId,
						dates,
						rate: pendingData.rate,
						status: pendingData.status as RecReviewStatus,
						notes: pendingData.notes || undefined,
						lastUpdatedBy: userName,
						allowOverwrite,
					},
				},
			});

			if (result.data?.createBulkRecs.success) {
				// Success case - reset form and close dialog
				toast.success(
					`Successfully created ${result.data.createBulkRecs.createdCount} rate recommendations`,
				);
				form.reset();
				setConfirmDialogOpen(false);
				setPendingData(null);
				setConflicts(null);
			} else if (
				result.data?.createBulkRecs.conflicts &&
				result.data.createBulkRecs.conflicts.length > 0 &&
				!allowOverwrite
			) {
				// Conflicts detected - show them in the dialog for user to decide
				setConflicts(result.data.createBulkRecs.conflicts);
				// Keep dialog open to show conflicts and allow overwrite option
			} else {
				// Other errors
				const errorMessage =
					result.data?.createBulkRecs.errors?.join(', ') ||
					'Unknown error occurred';
				toast.error(`Error: ${errorMessage}`);
				setConfirmDialogOpen(false);
				setPendingData(null);
				setConflicts(null);
			}
		} catch (error) {
			console.error('Bulk form submission error:', error);
			toast.error('Failed to create rate recommendations. Please try again.');
			setConfirmDialogOpen(false);
			setPendingData(null);
			setConflicts(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReset = () => {
		form.reset({
			startDate: getTodayAtMidnight(),
			endDate: (() => {
				const result = addDays(1, getTodayAtMidnight());
				return result?.toDate
					? result.toDate()
					: new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
			})(),
			rate: undefined,
			status: 'R',
			notes: '',
			daysOfWeek: daySelectionPresets.all,
		});
	};

	if (!isVisible) {
		return null;
	}

	return (
		<>
			<Card className='tw-w-full'>
				<CardHeader>
					<CardTitle className='tw-text-lg'>
						Bulk Rate Recommendations
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleFormSubmit)}
							className='tw-space-y-6'
						>
							{/* Date Range, Rate, and Status - All on one line */}
							<div className='tw-grid tw-grid-cols-4 tw-gap-4'>
								<div className='tw-col-span-2'>
									<DateRangeFields
										setValue={form.setValue}
										getValues={form.getValues}
										control={form.control}
									/>
								</div>
								<FormField
									control={form.control}
									name='rate'
									render={({ field }) => (
										<FormItem>
											<FormLabel htmlFor='rate-input'>Rate ($)</FormLabel>
											<div className='tw-relative'>
												<span className='tw-absolute tw-left-3 tw-top-2 tw-text-gray-500'>
													$
												</span>
												<Input
													id='rate-input'
													type='number'
													min='0'
													max='9999'
													step='0.01'
													placeholder='0.00'
													className='tw-pl-7'
													name={field.name}
													ref={field.ref}
													onBlur={field.onBlur}
													value={
														field.value === 0 || field.value === undefined
															? ''
															: field.value
													}
													onChange={(e) => {
														const value = e.target.value;
														if (value === '') {
															field.onChange(undefined);
														} else {
															const numValue = Number.parseFloat(value);
															field.onChange(
																Number.isNaN(numValue) ? undefined : numValue,
															);
														}
													}}
												/>
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name='status'
									render={({ field }) => (
										<FormItem>
											<FormLabel htmlFor='status-select'>Status</FormLabel>
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<SelectTrigger id='status-select'>
													<SelectValue placeholder='Select status' />
												</SelectTrigger>
												<SelectContent>
													{STATUS_OPTIONS.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{/* Notes */}
							<FormField
								control={form.control}
								name='notes'
								render={({ field }) => (
									<FormItem>
										<FormLabel htmlFor='notes-input'>
											Notes (Optional)
										</FormLabel>
										<Input
											{...field}
											id='notes-input'
											placeholder='Add any notes for these recommendations...'
										/>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Days of Week Selector */}
							<DaysOfWeekSelector
								control={form.control}
								setValue={form.setValue}
							/>

							{/* Form Actions */}
							<div className='tw-flex tw-justify-end tw-gap-3 tw-pt-4 tw-border-t'>
								<Button
									type='button'
									variant='outline'
									onClick={handleReset}
									disabled={!form.formState.isDirty}
								>
									Reset
								</Button>
								<Button
									type='submit'
									disabled={!form.formState.isValid || isSubmitting}
								>
									Create Recommendations
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<BulkConfirmationDialog
				open={confirmDialogOpen}
				onOpenChange={(open) => {
					setConfirmDialogOpen(open);
					if (!open) {
						setConflicts(null); // Clear conflicts when dialog closes
					}
				}}
				pendingData={pendingData}
				onConfirm={handleConfirm}
				isPending={isSubmitting}
				conflicts={conflicts}
			/>
		</>
	);
}
